import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { WordPopover } from '../WordPopover';

interface ClickableTextProps {
  text: string;
  onWordClick?: (word: string) => boolean | void; // Return false to delay popover
  className?: string;
  pendingWord?: string | null; // Word waiting for confirmation
  paragraphStartIndex?: number;
  ttsCharIndex?: number | null;
  ttsFullText?: string;
  sessionId?: string;
  sessionPartner?: { id: string; name: string };
  onSaveToSession?: (data: {
    word: string;
    isExpression: boolean;
    assignedTo: 'me' | 'friend' | 'both';
    details?: any;
  }) => Promise<void>;
  enableExpressionSelection?: boolean;
}

export const ClickableText: React.FC<ClickableTextProps> = ({
  text,
  onWordClick,
  className = '',
  pendingWord = null,
  paragraphStartIndex = 0,
  ttsCharIndex = null,
  sessionId,
  sessionPartner,
  onSaveToSession,
  enableExpressionSelection = false
}) => {
  const [selectedWord, setSelectedWord] = useState<string | null>(null);
  const [isExpressionMode, setIsExpressionMode] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState<{ top: number; left: number; maxHeight: number } | null>(null);
  const [pendingWordState, setPendingWordState] = useState<string | null>(null);
  const [pendingPosition, setPendingPosition] = useState<{ top: number; left: number; maxHeight: number } | null>(null);
  const [floatingExpression, setFloatingExpression] = useState<{ text: string; top: number; left: number } | null>(null);

  const cleanWord = (word: string) => {
    return word
      .replace(/'s\b/g, ' is')
      .replace(/'re\b/g, ' are')
      .replace(/'ve\b/g, ' have')
      .replace(/'d\b/g, ' would')
      .replace(/'ll\b/g, ' will')
      .replace(/'m\b/g, ' am')
      .replace(/―/g, '')
      .replace('...', '')
      .replace(/n't\b/g, ' not')
      .replace(/{\/?it}/g, '')
      .replace(/{\/?phrase}/g, '')
      .replace(/\bok\b/g, 'okay')
      .replace(/\bOK\b/g, 'okay')
      .replace(/\bcan't\b/g, 'cannot')
      .replace('=', '')
      .replace('[', '')
      .replace(']', '')
      .replace(';', '')
      .replace(/\bwon't\b/g, 'will not')
      .replace(/[.,!?]/g, '')
      .trim()
      .split(' ')[0];
  };

  const handleWordClick = (word: string, event: React.MouseEvent) => {
    const rect = (event.target as HTMLElement).getBoundingClientRect();
    const cleanedWord = cleanWord(word);
    
    // Calculate available space below and above the clicked word
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    const estimatedPopoverHeight = 400; // Estimated max height of popover
    const minPadding = 16; // Minimum padding from viewport edges
    
    let top: number;
    let maxHeight: number;
    
    // If there's not enough space below, position above the word
    if (spaceBelow < estimatedPopoverHeight && spaceAbove > spaceBelow) {
      top = rect.top - estimatedPopoverHeight - 8;
      // Calculate max height based on available space above
      maxHeight = Math.min(70 * viewportHeight / 100, rect.top - minPadding);
    } else {
      top = rect.bottom + 8;
      // Calculate max height based on available space below
      maxHeight = Math.min(70 * viewportHeight / 100, viewportHeight - rect.bottom - minPadding);
    }
    
    // Ensure popover doesn't go above viewport
    top = Math.max(minPadding, top);
    // Ensure maxHeight is at least reasonable
    maxHeight = Math.max(200, maxHeight);
    
    const position = {
      top,
      left: rect.left,
      maxHeight
    };
    
    // Call onWordClick first (may show confirmation modal)
    if (onWordClick) {
      const shouldShow = onWordClick(cleanedWord);
      // If onWordClick returns false, delay showing popover
      if (shouldShow === false) {
        setPendingWordState(cleanedWord);
        setPendingPosition(position);
        return;
      }
    }
    
    // Show popover immediately
    setIsExpressionMode(false);
    setFloatingExpression(null);
    setSelectedWord(cleanedWord);
    setPopoverPosition(position);
  };

  const handleMouseUp = () => {
    if (!enableExpressionSelection) return;
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed) return;

    const textSelected = selection.toString().trim();
    if (textSelected && textSelected.split(/\s+/).length > 1) {
      try {
        const range = selection.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        setFloatingExpression({
          text: textSelected,
          top: Math.max(16, rect.bottom + 6),
          left: Math.max(16, rect.left)
        });
      } catch (e) {
        // Fallback if range fails
      }
    }
  };

  const handleStudyExpression = (expr: string, pos: { top: number; left: number }) => {
    setIsExpressionMode(true);
    setSelectedWord(expr);
    setPopoverPosition({
      top: pos.top,
      left: pos.left,
      maxHeight: 400
    });
    setFloatingExpression(null);
    if (window.getSelection) {
      window.getSelection()?.removeAllRanges();
    }
  };

  // Show popover for pending word after confirmation
  useEffect(() => {
    if (pendingWord && pendingWord === pendingWordState && pendingPosition) {
      setIsExpressionMode(false);
      setSelectedWord(pendingWord);
      setPopoverPosition(pendingPosition);
      setPendingWordState(null);
      setPendingPosition(null);
    }
  }, [pendingWord, pendingWordState, pendingPosition]);

  const words = text.split(' ');
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  return (
    <>
      <span className={className} onMouseUp={handleMouseUp} onTouchEnd={handleMouseUp}>
        {words.map((word, i, arr) => {
          const wordStart = paragraphStartIndex + arr.slice(0, i).reduce((sum, w) => sum + w.length + 1, 0);
          const wordEnd = wordStart + word.length;
          // Highlight the whole SENTENCE that contains the active ttsCharIndex
          let isHighlighted = false;
          if (ttsCharIndex !== null && ttsCharIndex >= paragraphStartIndex && ttsCharIndex < paragraphStartIndex + text.length) {
            // Find the active sentence boundaries within this paragraph
            const localIndex = ttsCharIndex - paragraphStartIndex;
            
            // Find sentence start (look backwards for . ? !)
            let sentenceStart = 0;
            for (let j = localIndex; j >= 0; j--) {
              if ((text[j] === '.' || text[j] === '?' || text[j] === '!') && j < localIndex - 1) {
                sentenceStart = j + 1;
                break;
              }
            }
            
            // Find sentence end (look forwards for . ? !)
            let sentenceEnd = text.length;
            for (let j = localIndex; j < text.length; j++) {
              if (text[j] === '.' || text[j] === '?' || text[j] === '!') {
                sentenceEnd = j + 1;
                break;
              }
            }
            
            const localWordStart = wordStart - paragraphStartIndex;
            const localWordEnd = wordEnd - paragraphStartIndex;
            
            // If this word is within the active sentence, highlight it!
            if (localWordStart >= sentenceStart && localWordEnd <= sentenceEnd + 1) {
              isHighlighted = true;
            }
          }
          return (
            <span key={i} className={isHighlighted ? 'bg-yellow-200 text-black' : ''}>
              <span
                className={`cursor-pointer transition-colors ${isHighlighted ? '' : 'hover:underline hover:text-indigo-600'}`}
                onClick={(e) => handleWordClick(word, e)}
              >
                {word}
              </span>
              {i < arr.length - 1 && ' '}
            </span>
          );
        })}
      </span>

      {/* Floating Expression Study Button */}
      {floatingExpression && createPortal(
        <div
          className="fixed z-[9999] bg-indigo-600 text-white shadow-xl rounded-full py-1.5 pl-3.5 pr-2 text-xs font-semibold flex items-center gap-2 cursor-pointer hover:bg-indigo-700 animate-in fade-in zoom-in-95 duration-150"
          style={{
            top: `${floatingExpression.top}px`,
            left: `clamp(12px, ${floatingExpression.left}px, calc(100vw - 230px))`,
          }}
          onClick={() => handleStudyExpression(floatingExpression.text, floatingExpression)}
        >
          <span>✨ Study Expression:</span>
          <span className="max-w-[130px] sm:max-w-[180px] truncate underline italic">"{floatingExpression.text}"</span>
          <button
            type="button"
            aria-label="Close Study Expression"
            className="p-1 -mr-0.5 text-white/80 hover:text-white hover:bg-white/20 rounded-full transition-colors flex items-center justify-center shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              setFloatingExpression(null);
              if (window.getSelection) {
                window.getSelection()?.removeAllRanges();
              }
            }}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>,
        document.body
      )}

      {selectedWord && popoverPosition && createPortal(
        <>
          <div 
            className="fixed inset-0 z-[9998] bg-black/40 backdrop-blur-xs md:hidden" 
            onClick={() => {
              setSelectedWord(null);
              setPopoverPosition(null);
              setIsExpressionMode(false);
            }} 
          />
          <div
            className="fixed z-[9999] max-md:left-1/2 max-md:top-1/2 max-md:-translate-x-1/2 max-md:-translate-y-1/2 max-md:w-[calc(100vw-24px)] max-md:max-w-[420px] max-md:max-h-[85vh] max-md:overflow-y-auto"
            style={{
              ...(isMobile
                ? {}
                : {
                    left: `clamp(16px, ${popoverPosition.left}px, calc(100vw - 440px))`,
                    top: `${popoverPosition.top}px`,
                    maxWidth: '440px',
                    maxHeight: `${popoverPosition.maxHeight}px`,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                  }),
            }}
          >
            <div className="w-full h-full">
              <WordPopover
                word={selectedWord}
                context={text}
                position={{ top: 0, left: 0 }}
                useRelativePosition={true}
                isExpression={isExpressionMode}
                sessionId={sessionId}
                sessionPartner={sessionPartner}
                onSaveToSession={onSaveToSession}
                onClose={() => {
                  setSelectedWord(null);
                  setPopoverPosition(null);
                  setIsExpressionMode(false);
                }}
              />
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
};

